import { useAuthFrontendApis } from "@propelauth/frontend-apis-react";
import { Button } from "components/ui/button";
import RootlyFullLogo from "components/ui/icons/rootly_logo";
import TierZeroMark from "components/ui/icons/tierzero_mark";
import { Input } from "components/ui/input";
import { Mail } from "lucide-react";
import React, { useCallback, useState } from "react";
import { MAGIC_LINK_PENDING_KEY } from "src/pages/HomePage";
import {
  getBrandThemeFromDomain,
  getProductName,
  getSupportEmail,
  isRootlyBranded,
} from "src/utils/brandTheme";

type LoginState = "email_entry" | "loading" | "magic_link_sent" | "error";

const LoginPage: React.FC = () => {
  const { loginViaSamlForOrg, passwordlessLogin } = useAuthFrontendApis();
  const [email, setEmail] = useState("");
  const [loginState, setLoginState] = useState<LoginState>("email_entry");
  const [errorMessage, setErrorMessage] = useState("");

  const brandTheme = getBrandThemeFromDomain();
  const productName = getProductName(brandTheme);
  const supportEmail = getSupportEmail(brandTheme);
  const isRootly = isRootlyBranded(brandTheme);

  const handleSubmit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();

      const trimmedEmail = email.trim().toLowerCase();
      if (!trimmedEmail || !trimmedEmail.includes("@")) {
        setErrorMessage("Please enter a valid email address.");
        setLoginState("error");
        return;
      }

      setLoginState("loading");
      setErrorMessage("");

      try {
        let samlRedirected = false;
        const samlResponse = await loginViaSamlForOrg({ email: trimmedEmail });

        await samlResponse.handle({
          success(data) {
            samlRedirected = true;
            window.location.href = data.login_url;
          },
          orgNotFound() {
            // No SAML for this domain — fall through to magic link
          },
          unexpectedOrUnhandled(error) {
            console.warn("SAML check error, falling back to magic link:", error);
          },
        });

        if (samlRedirected) {
          return;
        }

        const magicLinkResponse = await passwordlessLogin({
          email: trimmedEmail,
          create_if_doesnt_exist: true,
        });

        await magicLinkResponse.handle({
          success() {
            try {
              localStorage.setItem(MAGIC_LINK_PENDING_KEY, Date.now().toString());
            } catch {
              // sessionStorage blocked — login still works, just no auto-detect
            }
            setLoginState("magic_link_sent");
          },
          passwordlessLoginDisabled() {
            setErrorMessage(`Passwordless login is not enabled. Please reach out to ${supportEmail}.`);
            setLoginState("error");
          },
          cannotSignupWithPersonalEmail() {
            setErrorMessage("Please use your work email address.");
            setLoginState("error");
          },
          domainNotAllowed() {
            setErrorMessage(`This email domain is not allowed. Please reach out to ${supportEmail}.`);
            setLoginState("error");
          },
          userAccountDisabled() {
            setErrorMessage(`Your account has been disabled. Please reach out to ${supportEmail}.`);
            setLoginState("error");
          },
          userAccountLocked() {
            setErrorMessage(`Your account has been locked. Please reach out to ${supportEmail}.`);
            setLoginState("error");
          },
          badRequest(error) {
            const fieldErrors = error.user_facing_errors;
            const emailError = fieldErrors?.email;
            setErrorMessage(emailError || "Invalid email address.");
            setLoginState("error");
          },
          unexpectedOrUnhandled() {
            setErrorMessage(`Something went wrong. Please reach out to ${supportEmail}.`);
            setLoginState("error");
          },
        });
      } catch {
        setErrorMessage(`Something went wrong. Please reach out to ${supportEmail}.`);
        setLoginState("error");
      }
    },
    [email, loginViaSamlForOrg, passwordlessLogin],
  );

  const handleResend = useCallback(() => {
    setLoginState("email_entry");
  }, []);

  if (loginState === "magic_link_sent") {
    return (
      <div className="min-h-screen bg-[#111111] text-white flex items-center justify-center p-4">
        <div className="w-full max-w-lg text-center">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-[#3B82F6]/20 mb-6">
            <Mail className="w-8 h-8 text-[#3B82F6]" />
          </div>
          <h1 className="text-2xl font-bold mb-3">Check your email</h1>
          <p className="text-neutral-400 mb-2">
            We sent a sign-in link to
          </p>
          <p className="text-white font-medium mb-6">{email.trim().toLowerCase()}</p>
          <p className="text-neutral-500 text-sm mb-6">
            Click the link in the email to sign in. The link expires in 1 hour.
          </p>
          <button
            onClick={handleResend}
            className="text-[#3B82F6] hover:text-[#3B82F6]/80 text-sm underline underline-offset-2 transition-colors"
          >
            Use a different email
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#111111] text-white flex items-center justify-center p-4">
      <div className="w-full max-w-lg">
        {/* Logo */}
        <div className="flex justify-center mb-6">
          {isRootly ? (
            <RootlyFullLogo className="h-10 w-auto" />
          ) : (
            <TierZeroMark className="h-14 w-auto text-[#3B82F6]" />
          )}
        </div>

        {/* Heading */}
        <h1 className="text-2xl font-bold mb-8 text-center">
          Log in to {productName}
        </h1>

        {/* Form */}
        <form onSubmit={handleSubmit}>
          <div className="rounded-xl border border-neutral-700/60 bg-neutral-900/50 p-6">
            <Input
              type="email"
              placeholder="name@company.com"
              value={email}
              onChange={(e) => {
                setEmail(e.target.value);
                if (loginState === "error") {
                  setLoginState("email_entry");
                  setErrorMessage("");
                }
              }}
              className="w-full bg-neutral-800/80 border-neutral-700 text-white placeholder:text-neutral-500 h-12 text-base"
              autoFocus
              autoComplete="email"
            />
            {loginState === "error" && errorMessage && (
              <p className="text-red-400 text-sm mt-3">{errorMessage}</p>
            )}
          </div>

          <Button
            type="submit"
            size="lg"
            className="w-full mt-4 bg-[#3B82F6] hover:bg-[#2563EB] text-white font-medium"
            loading={loginState === "loading"}

            disabled={loginState === "loading"}
          >
            Continue
          </Button>
        </form>
      </div>
    </div>
  );
};

export default LoginPage;
